import React, { useState, useEffect, useRef, useCallback } from "react";
import styled from "styled-components";
import TextField from "./TextField";
import { DragIcon, PlusIcon, DeleteIcon } from "./Icons";
import IconButton from "./Button";
import DropIndicator from "./DropIndicator";

const AnswerList = ({ answers, onAnswersChange }) => {
  const [draggingAnswerIndex, setDraggingAnswerIndex] = useState(null);
  const scrollSpeed = 20;
  const scrollThreshold = 100;
  const autoScrollInterval = useRef(null);
  const clientYRef = useRef(0);

  // Auto-scroll functions
  const startAutoScroll = useCallback(() => {
    if (autoScrollInterval.current) return;

    autoScrollInterval.current = setInterval(() => {
      const { innerHeight } = window;
      const clientY = clientYRef.current;

      if (clientY < scrollThreshold) {
        window.scrollBy(0, -scrollSpeed);
      } else if (clientY > innerHeight - scrollThreshold) {
        window.scrollBy(0, scrollSpeed);
      }
    }, 16);
  }, [scrollThreshold, scrollSpeed]);

  const stopAutoScroll = useCallback(() => {
    if (autoScrollInterval.current) {
      clearInterval(autoScrollInterval.current);
      autoScrollInterval.current = null;
    }
  }, []);

  const handleDragOver = useCallback(
    (e) => {
      e.preventDefault();

      clientYRef.current = e.clientY;

      const { innerHeight } = window;

      if (
        e.clientY < scrollThreshold ||
        e.clientY > innerHeight - scrollThreshold
      ) {
        startAutoScroll();
      } else {
        stopAutoScroll();
      }
    },
    [scrollThreshold, startAutoScroll, stopAutoScroll]
  );

  const handleDropOutside = useCallback(
    (e) => {
      e.preventDefault();
      stopAutoScroll();
    },
    [stopAutoScroll]
  );

  const handleDragEnd = useCallback(() => {
    setDraggingAnswerIndex(null);

    document.removeEventListener("dragover", handleDragOver, {
      passive: false,
    });
    document.removeEventListener("dragend", handleDragEnd);
    document.removeEventListener("drop", handleDropOutside);

    stopAutoScroll();
  }, [handleDragOver, handleDropOutside, stopAutoScroll]);

  const handleDragStart = useCallback(
    (index) => {
      setDraggingAnswerIndex(index);

      document.addEventListener("dragover", handleDragOver, { passive: false });
      document.addEventListener("dragend", handleDragEnd);
      document.addEventListener("drop", handleDropOutside);
    },
    [handleDragOver, handleDragEnd, handleDropOutside]
  );

  useEffect(() => {
    return () => {
      stopAutoScroll();
      document.removeEventListener("dragover", handleDragOver, {
        passive: false,
      });
      document.removeEventListener("dragend", handleDragEnd);
      document.removeEventListener("drop", handleDropOutside);
    };
  }, [stopAutoScroll, handleDragOver, handleDragEnd, handleDropOutside]);

  // Drop Indicator Handlers
  const handleDropIndicatorDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.style.opacity = "1";
  };

  const handleDropIndicatorDragLeave = (e) => {
    e.currentTarget.style.opacity = "0";
  };

  const handleDropIndicatorDrop = (e, beforeIndex) => {
    e.preventDefault();
    e.currentTarget.style.opacity = "0";

    stopAutoScroll();

    if (draggingAnswerIndex === null) return;

    const fromIndex = draggingAnswerIndex;
    if (fromIndex === -1) return;

    let updatedAnswers = [...answers];

    if (beforeIndex === fromIndex) {
      return;
    }

    let dropIndex;
    if (beforeIndex === null) {
      // Drop at the end
      dropIndex = updatedAnswers.length;
    } else {
      dropIndex = beforeIndex;
    }

    if (dropIndex > fromIndex) {
      dropIndex--;
    }

    const [draggedAnswer] = updatedAnswers.splice(fromIndex, 1);
    updatedAnswers.splice(dropIndex, 0, draggedAnswer);

    onAnswersChange(updatedAnswers);
    setDraggingAnswerIndex(null);
  };

  // Existing handlers for adding, changing, and deleting answers
  const handleAddAnswer = () => {
    const newAnswers = answers ? [...answers, ""] : [""]; // Ensure answers is an array
    onAnswersChange(newAnswers);
  };

  const handleAnswerChange = (index, value) => {
    const updatedAnswers = [...answers];
    updatedAnswers[index] = value;
    onAnswersChange(updatedAnswers);
  };

  const handleDelete = (index) => {
    const updatedAnswers = answers.filter((_, idx) => idx !== index);
    onAnswersChange(updatedAnswers);
  };

  return (
    <div>
      {answers &&
        answers.map((answer, index) => (
          <React.Fragment key={index}>
            <DropIndicator
              beforeItemId={index}
              onDragOver={handleDropIndicatorDragOver}
              onDragLeave={handleDropIndicatorDragLeave}
              onDrop={(e) => handleDropIndicatorDrop(e, index)}
            />
            <DraggableItem
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragEnd={handleDragEnd}
            >
              <DragIcon />
              <TextInputWrapper>
                <TextField
                  value={answer}
                  onChange={(e) => handleAnswerChange(index, e.target.value)}
                  placeholder="Enter answer here"
                  maxLength={250}
                  showCharacterLimit={true}
                  isTextArea={true}
                />
              </TextInputWrapper>

              <DeleteButton
                onClick={(e) => {
                  e.preventDefault();
                  handleDelete(index);
                }}
              >
                <DeleteIcon />
              </DeleteButton>
            </DraggableItem>
          </React.Fragment>
        ))}
      {/* DropIndicator at the end */}
      <DropIndicator
        beforeItemId={null}
        onDragOver={handleDropIndicatorDragOver}
        onDragLeave={handleDropIndicatorDragLeave}
        onDrop={(e) => handleDropIndicatorDrop(e, null)}
      />
      <IconButton
        variant="ghost"
        state="idle"
        icon={<PlusIcon color="#004F96" />}
        onClick={handleAddAnswer}
      >
        Add another answer
      </IconButton>
    </div>
  );
};

export default AnswerList;

// Styled components
const DraggableItem = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  margin-bottom: 10px;
  background: #f8f8f8;
  padding: 10px;
  border-radius: 8px;
  transition: opacity 0.3s, box-shadow 0.3s;
  cursor: grab;

  &:hover {
    cursor: grab;
  }

  &.dragging {
    opacity: 0.5;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
    cursor: grabbing;
  }
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  color: #d9534f;
  cursor: pointer;
  font-size: 16px;

  &:hover {
    color: #b52a1e;
  }
`;

const TextInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 16px;
  width: 100%;
`;
